/**
 * <product-card-video>
 *
 * Plays a product's video as the media of a product card, and quietly keeps the
 * card image instead whenever playback is not possible or not wanted:
 *
 *   - no JavaScript, or an old browser without customElements
 *   - iOS/iPadOS Low Power Mode, which rejects programmatic play()
 *   - any other autoplay policy that blocks muted inline video
 *   - "Reduce Motion" / data saver / very slow connections
 *   - unsupported codec, decode or network errors
 *   - playback that starts and then stops on its own
 *
 * The fallback image is always in the DOM underneath the video, so the fallback
 * is simply "never reveal the video". Nothing is swapped in, so there is no
 * layout shift and no flash of an empty frame.
 */
(() => {
  if (typeof customElements === 'undefined' || customElements.get('product-card-video')) return;

  /** How long playback gets to actually start before we give up on it. */
  const PLAYBACK_TIMEOUT = 3000;

  class ProductCardVideo extends HTMLElement {
    constructor() {
      super();

      this.video = null;
      this.observer = null;
      this.watchdog = null;

      this.failed = false;
      this.revealed = false;
      this.inView = false;
      this.pausedByUs = false;
      this.resumeAttempted = false;

      this.handleTimeUpdate = this.handleTimeUpdate.bind(this);
      this.handleError = this.handleError.bind(this);
      this.handlePause = this.handlePause.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    }

    connectedCallback() {
      this.video = this.querySelector('video');

      if (!this.video || !this.canPlayVideo()) {
        this.fallBackToImage();
        return;
      }

      // `error` on a <source> does not bubble, so listen in the capture phase.
      this.video.addEventListener('error', this.handleError, true);
      this.video.addEventListener('timeupdate', this.handleTimeUpdate);
      this.video.addEventListener('pause', this.handlePause);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);

      this.watchViewport();
    }

    disconnectedCallback() {
      this.clearWatchdog();

      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      document.removeEventListener('visibilitychange', this.handleVisibilityChange);

      if (this.video) {
        this.video.removeEventListener('error', this.handleError, true);
        this.video.removeEventListener('timeupdate', this.handleTimeUpdate);
        this.video.removeEventListener('pause', this.handlePause);
        if (!this.video.paused) this.video.pause();
      }
    }

    /**
     * Cheap up-front checks. Anything that cannot be known in advance (Low Power
     * Mode, autoplay policy, decode failures) is caught later by play() rejecting
     * or by the watchdog.
     */
    canPlayVideo() {
      const video = this.video;

      if (typeof video.play !== 'function' || typeof video.canPlayType !== 'function') return false;
      if (!video.canPlayType('video/mp4')) return false;

      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;

      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (connection) {
        if (connection.saveData) return false;
        if (typeof connection.effectiveType === 'string' && /2g$/.test(connection.effectiveType)) return false;
      }

      return true;
    }

    watchViewport() {
      if (!('IntersectionObserver' in window)) {
        this.inView = true;
        this.play();
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.inView = entry.isIntersecting;
            if (entry.isIntersecting) {
              this.play();
            } else {
              this.suspend();
            }
          });
        },
        { rootMargin: '100px 0px', threshold: 0.2 }
      );

      this.observer.observe(this);
    }

    play() {
      if (this.failed || !this.video) return;
      if (document.visibilityState === 'hidden') return;

      this.pausedByUs = false;
      this.startWatchdog();

      const playback = this.video.play();

      if (playback && typeof playback.catch === 'function') {
        playback.catch((error) => {
          // Pausing right after play() (fast scrolling, tab switch) aborts the
          // request; that is not a playback failure.
          if (this.pausedByUs || (error && error.name === 'AbortError')) return;
          this.fallBackToImage();
        });
      }
    }

    /** Out of view or backgrounded: stop playback, but keep the video usable. */
    suspend() {
      this.clearWatchdog();
      this.pausedByUs = true;
      if (this.video && !this.video.paused) this.video.pause();
    }

    startWatchdog() {
      if (this.watchdog || this.revealed) return;

      this.watchdog = setTimeout(() => {
        this.watchdog = null;
        if (this.revealed || this.failed || this.pausedByUs) return;

        // Some browsers resolve play() but never actually advance the video
        // (battery savers, throttled background rendering).
        if (this.video.currentTime > 0 && !this.video.paused) {
          this.reveal();
        } else {
          this.fallBackToImage();
        }
      }, PLAYBACK_TIMEOUT);
    }

    clearWatchdog() {
      if (!this.watchdog) return;
      clearTimeout(this.watchdog);
      this.watchdog = null;
    }

    handleTimeUpdate() {
      if (this.revealed || this.failed) return;
      if (this.video.currentTime > 0.05 && !this.video.paused) this.reveal();
    }

    handleError() {
      this.fallBackToImage();
    }

    handlePause() {
      if (this.failed || this.pausedByUs) return;
      if (document.visibilityState === 'hidden') return;

      // The video stopped on its own - e.g. Low Power Mode switched on while the
      // page was open. Give it one more try, then settle on the image.
      if (this.resumeAttempted) {
        this.fallBackToImage();
        return;
      }

      this.resumeAttempted = true;
      this.play();
    }

    handleVisibilityChange() {
      if (document.visibilityState === 'hidden') {
        this.suspend();
      } else if (this.inView) {
        this.play();
      }
    }

    reveal() {
      this.clearWatchdog();
      this.revealed = true;
      this.resumeAttempted = false;
      this.dataset.state = 'playing';
    }

    /** Permanent for this page view: hide the video and release it. */
    fallBackToImage() {
      if (this.failed) return;

      this.failed = true;
      this.revealed = false;
      this.clearWatchdog();
      this.dataset.state = 'unavailable';

      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      if (this.video) {
        this.video.removeEventListener('error', this.handleError, true);
        this.video.removeEventListener('timeupdate', this.handleTimeUpdate);
        this.video.removeEventListener('pause', this.handlePause);
        if (!this.video.paused) this.video.pause();
        this.video.remove();
        this.video = null;
      }
    }
  }

  customElements.define('product-card-video', ProductCardVideo);
})();
